// Types
import { useCallback, useEffect, useMemo } from 'react';
import { useReservationStatusEnabled } from 'hooks/useFeatureFlag';
import { lowerCase, startCase } from 'lodash';
// Slices
import { fieldMappingSelector } from 'slices/field.slice';
import {
  selectObjectTypesList,
  selectObjectTypesMap,
} from 'slices/objectTypes.slice';
import {
  fetchReservationModes,
  reservationModesSelector,
} from 'slices/reservationMode.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
// Utils
import { createTypeOptions } from 'utils/objectTypes';
// Types
import {
  EFieldType,
  EReportTemplateType,
  EReportingCapacitySource,
  EReportingTimeType,
  EReservationStatuses,
} from '@timeedit/types';
import { Segmented, TEDrawerSection, TEInput } from '@timeedit/ui-components';
import { DefaultOptionType } from '@timeedit/ui-components/antd/select/index';
import DemandSizeSettings from './DemandSizeSettings';
import { TReportTemplateProps } from './ReportTemplate';

const capacitySourceOptions = Object.entries(EReportingCapacitySource).map(
  ([key, value]) => ({
    label: startCase(lowerCase(value)),
    value: key,
  })
);

const DataSource = ({ data, onChange }: TReportTemplateProps) => {
  const fieldMapping = useAppSelector(fieldMappingSelector);
  const objectTypesMap = useAppSelector(selectObjectTypesMap);
  const objectTypes = useAppSelector(selectObjectTypesList);
  const reservationModes = useAppSelector(reservationModesSelector);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchReservationModes());
  }, [dispatch]);

  const getFieldOptions = useCallback(
    (objectId?: number, useExtId: boolean = false) => {
      const objectType =
        objectId !== undefined ? objectTypesMap?.[objectId] : undefined;

      if (!objectType || !fieldMapping) {
        return [];
      }

      const { parentFields = [], fields = [] } = objectType;
      const fieldOptions: DefaultOptionType[] = [];

      [...parentFields, ...fields].forEach((item) => {
        const field = fieldMapping[item];

        if (field?.fieldType !== EFieldType.INTEGER) {
          return;
        }

        fieldOptions.push({
          value: useExtId ? field.extId : field.id,
          label: field.extId,
        });
      });

      return fieldOptions;
    },
    [fieldMapping, objectTypesMap]
  );

  const targetFieldOptions = useMemo(
    () => getFieldOptions(data?.targetObjectType),
    [data?.targetObjectType, getFieldOptions]
  );

  const sizeFieldOptions = useMemo(
    () => getFieldOptions(data?.targetObjectType, true),
    [data?.targetObjectType, getFieldOptions]
  );

  const capacitySourceFieldOptions = useMemo(
    () => getFieldOptions(data?.capacityObject),
    [data?.capacityObject, getFieldOptions]
  );

  const objectTypesOptions = useMemo(
    () => createTypeOptions(objectTypes),
    [objectTypes]
  );

  const reservationModesOptions = useMemo(() => {
    return reservationModes.map((mode) => ({
      label: mode.name,
      value: mode.id,
    }));
  }, [reservationModes]);

  const onTargetObjectTypeChange = useCallback(
    (val: number) => {
      onChange('targetObjectType', val);
      onChange('maxHoursField', undefined);
      onChange('capacityField', undefined);
      onChange('fieldSettings', []);
    },
    [onChange]
  );

  return (
    <TEDrawerSection label="Data Source">
      <div className="section-wrapper">
        <div className="section-col">
          {[
            EReportTemplateType.UTILIZATION,
            EReportTemplateType.LOAD,
            EReportTemplateType.HEATMAP,
          ].includes(data.templateType) && (
            <>
              <TEInput.Select
                label="Reservation Modes"
                tooltip="Reservation modes that are included in report"
                value={data.reservationModes}
                options={reservationModesOptions}
                onChange={(val) => onChange('reservationModes', val)}
                placeholder="All"
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                data-testid="report-template-reservationModes"
              />

              <TEInput.Select
                label="Reservation Statuses"
                tooltip="Reservation statuses that are included in report"
                value={data.reservationStatuses}
                options={reservationStatusesOptions}
                onChange={(val) => onChange('reservationStatuses', val)}
                placeholder="Default"
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                data-testid="report-template-reservationStatuses"
              />
            </>
          )}
          <TEInput.TreeSelect
            label="Target Object Type"
            tooltip="Object type that is target of report"
            value={data.targetObjectType}
            treeData={objectTypesOptions}
            treeDefaultExpandAll
            onChange={(val) => onTargetObjectTypeChange(val)}
            placeholder="Target Object Type"
            data-testid="report-template-targetObjectType"
          />
          {data.availableType === EReportingTimeType.MAX_HOURS && (
            <TEInput.Select
              label="Max Hours Field"
              tooltip="Field that contains the max hours available of target object"
              value={data.maxHoursField}
              options={targetFieldOptions}
              onChange={(val) => onChange('maxHoursField', val)}
              placeholder="Max Hours Field"
              data-testid="report-template-maxHoursField"
            />
          )}
          {[
            EReportTemplateType.UTILIZATION,
            EReportTemplateType.HEATMAP,
          ].includes(data.templateType) && (
            <>
              <TEInput.Select
                label="Capacity Field"
                tooltip="Field that contains the capacity of target object"
                value={data.capacityField}
                options={targetFieldOptions}
                onChange={(val) => onChange('capacityField', val)}
                placeholder="Capacity Field"
                data-testid="report-template-capacityField"
              />
              <TEInput.LabelWrapper
                label="Capacity Source"
                tooltip="Capacity can be calculated from field or from membership"
              >
                <Segmented
                  className="report-template-capacity-source-segmented"
                  value={data.capacitySource}
                  options={capacitySourceOptions}
                  onChange={(val) => onChange('capacitySource', val)}
                />
              </TEInput.LabelWrapper>
              <TEInput.TreeSelect
                label="Group Object"
                tooltip="Object type that contains the capacity of group"
                value={data.capacityObject}
                treeData={objectTypesOptions}
                treeDefaultExpandAll
                onChange={(val) => onChange('capacityObject', val)}
                placeholder="Group Object"
                data-testid="report-template-capacityObject"
              />
              {data.capacitySource === EReportingCapacitySource.FROM_FIELD && (
                <TEInput.Select
                  label="Size field"
                  tooltip="Field of group object that contains the capacity of group"
                  value={data.capacitySourceField}
                  options={capacitySourceFieldOptions}
                  onChange={(val) => onChange('capacitySourceField', val)}
                  placeholder="Size field"
                  data-testid="report-template-size-field"
                />
              )}
            </>
          )}
          {data.templateType === EReportTemplateType.DEMAND_ANALYTIC && (
            <DemandSizeSettings
              data={data}
              onChange={onChange}
              sizeFieldOptions={sizeFieldOptions}
            />
          )}
        </div>
      </div>
    </TEDrawerSection>
  );
};

export default DataSource;

const reservationStatusesOptions = Object.entries(EReservationStatuses)
  .map(([key, value]) => ({
    label: key,
    value,
  }))
  .filter((option) => option.value !== EReservationStatuses.UNKNOWN);
